import React, { useEffect, useState, useRef, useCallback } from 'react';
import JSZip from 'jszip';
import { api } from '../utils/api';
import { useToast } from '../components/Toast';
import { toEasternDate } from '../utils/time';

// ── Facebook export parsers ──────────────────────────────────────────────────
function extractPostItem(item) {
  const found = (item.data || []).find(d => typeof d.post === 'string' && d.post.trim().length > 10);
  if (!found) return null;
  return {
    content: found.post.trim(),
    date:    item.timestamp
               ? new Date(item.timestamp * 1000).toISOString().slice(0, 10)
               : new Date().toISOString().slice(0, 10),
    id:      `fb-${item.timestamp || Math.random().toString(36).slice(2)}`,
  };
}

function parseFBJson(json) {
  let items = json;
  if (!Array.isArray(json)) {
    items = json.your_posts_and_activity || json.posts || json.status_updates || [];
  }
  return Array.isArray(items) ? items.map(extractPostItem).filter(Boolean) : [];
}

function isPostFile(path) {
  const p = path.toLowerCase().replace(/\\/g, '/');
  return (p.includes('/posts/') || p.includes('your_posts') ||
          p.includes('posts_and_reels') || p.includes('status_updates')) && p.endsWith('.json');
}

async function parseFolder(files) {
  const candidates = Array.from(files).filter(f => isPostFile(f.webkitRelativePath || f.name));
  const targets = candidates.length > 0
    ? candidates
    : Array.from(files).filter(f => f.name.endsWith('.json'));
  const posts = [];
  for (const file of targets) {
    try { posts.push(...parseFBJson(JSON.parse(await file.text()))); } catch { /* skip */ }
  }
  return posts;
}

async function parseZip(zipFile) {
  const zip   = await JSZip.loadAsync(zipFile);
  const names = Object.keys(zip.files).filter(n => !zip.files[n].dir);
  const targets = names.filter(isPostFile).length > 0 ? names.filter(isPostFile) : names.filter(n => n.endsWith('.json'));
  const posts = [];
  for (const name of targets) {
    try { posts.push(...parseFBJson(JSON.parse(await zip.files[name].async('string')))); } catch { /* skip */ }
  }
  return posts;
}

// ── Progress bar shown during analysis ──────────────────────────────────────
function AnalysisProgressBar({ onComplete }) {
  const [elapsed, setElapsed]   = useState(0);
  const [done, setDone]         = useState(false);
  const DURATION = 65; // seconds

  useEffect(() => {
    const start = Date.now();

    // Tick the progress bar every 500ms
    const ticker = setInterval(() => {
      const secs = Math.floor((Date.now() - start) / 1000);
      setElapsed(secs);
      if (secs >= DURATION) clearInterval(ticker);
    }, 500);

    // Poll the API every 10s to catch early completion
    const poller = setInterval(async () => {
      try {
        const r = await api.getBrandVoice();
        if (r.profile) {
          clearInterval(ticker);
          clearInterval(poller);
          setDone(true);
          setTimeout(() => onComplete(r.profile), 600);
        }
      } catch { /* ignore poll errors */ }
    }, 10000);

    // Hard stop at 65s — final poll
    const deadline = setTimeout(async () => {
      clearInterval(ticker);
      clearInterval(poller);
      try {
        const r = await api.getBrandVoice();
        if (r.profile) { setDone(true); setTimeout(() => onComplete(r.profile), 600); }
      } catch { /* ignore */ }
    }, DURATION * 1000);

    return () => { clearInterval(ticker); clearInterval(poller); clearTimeout(deadline); };
  }, [onComplete]);

  const pct       = done ? 100 : Math.min(95, Math.round((elapsed / DURATION) * 100));
  const remaining = Math.max(0, DURATION - elapsed);
  const label     = done
    ? 'Analysis complete!'
    : remaining > 0
      ? `Analyzing with AI… ~${remaining}s remaining`
      : 'Wrapping up…';

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: 'var(--muted)', marginBottom: 6 }}>
        <span>{label}</span>
        <span>{pct}%</span>
      </div>
      <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 9999, height: 8, overflow: 'hidden' }}>
        <div style={{
          width:      `${pct}%`,
          height:     '100%',
          background: done ? 'var(--teal)' : 'linear-gradient(90deg, var(--teal), #4aa8a8)',
          borderRadius: 9999,
          transition: done ? 'width 0.4s ease' : 'width 0.6s linear',
        }} />
      </div>
      {!done && (
        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: '0.8rem', color: 'var(--muted)' }}>
          {[
            { label: 'Reading posts',      active: elapsed < 10 },
            { label: 'Identifying themes', active: elapsed >= 10 && elapsed < 30 },
            { label: 'Building voice',     active: elapsed >= 30 && elapsed < 55 },
            { label: 'Saving profile',     active: elapsed >= 55 },
          ].map(s => (
            <span key={s.label} style={{ opacity: s.active ? 1 : 0.4, fontWeight: s.active ? 600 : 400, transition: 'opacity 0.5s' }}>
              {s.active ? '▶ ' : '· '}{s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Paste placeholder ────────────────────────────────────────────────────────
const PLACEHOLDER = `Paste your Facebook posts here — one post per line, or separate multiple posts with ---

Example:
Our caregivers go above and beyond every single day. ❤️ We are so grateful for the dedication they bring to every home. #HomeCare #Michigan

---

Did you know that staying socially connected is one of the best things seniors can do for brain health? HCA provides compassionate companion care to keep your loved one engaged. Call us to learn more! 🏠 #SeniorCare #AgingInPlace`;

// ── Reel Assets section ───────────────────────────────────────────────────────
function ReelAssetsSection() {
  const toast                       = useToast();
  const [assets, setAssets]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [uploading, setUploading]   = useState(null); // which slot is uploading

  const load = useCallback(async () => {
    setLoading(true);
    try { setAssets(await api.getReelAssets()); }
    catch { toast('Failed to load reel assets', 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const upload = async (type, file) => {
    if (!file) return;
    setUploading(type);
    try {
      const { upload_url } = await api.getReelUploadUrl(type, file.name);
      const ct = type === 'music' ? 'audio/mpeg'
               : type === 'ffmpeg' ? 'application/octet-stream'
               : /\.png$/i.test(file.name) ? 'image/png' : 'image/jpeg';
      const res = await fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': ct }, body: file });
      if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
      await load();
      toast(`${file.name} uploaded successfully`, 'success');
    } catch (e) {
      toast(e.message || 'Upload failed', 'error');
    } finally {
      setUploading(null);
    }
  };

  const remove = async (key, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await api.deleteReelAsset(key);
      await load();
      toast(`${name} deleted`, 'success');
    } catch (e) {
      toast(e.message || 'Delete failed', 'error');
    }
  };

  const pickFile = (type, accept) => {
    const input = document.createElement('input');
    input.type   = 'file';
    input.accept = accept;
    input.onchange = (e) => upload(type, e.target.files[0]);
    input.click();
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes >= 1_048_576) return ` · ${(bytes / 1_048_576).toFixed(1)} MB`;
    return ` · ${(bytes / 1024).toFixed(0)} KB`;
  };

  const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' };
  const labelStyle = { fontSize: '0.85rem', fontWeight: 500 };
  const mutedStyle = { fontSize: '0.78rem', color: 'var(--muted)' };

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div className="card-header">
        <h3>Reel Assets</h3>
        <p style={{ fontSize: '0.82rem', color: 'var(--muted)', margin: 0 }}>
          Files the AI uses to generate animated Reels
        </p>
      </div>
      <div className="card-body">
        {loading ? <p style={{ color: 'var(--muted)' }}>Loading…</p> : (
          <>
            {/* FFmpeg Engine */}
            <p style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Video Engine</p>
            <div style={{ ...rowStyle, marginBottom: 16 }}>
              <div>
                <span style={labelStyle}>FFmpeg Binary</span>
                <span style={{ ...mutedStyle, marginLeft: 8 }}>
                  {assets?.ffmpeg?.installed
                    ? <span style={{ color: 'var(--teal)', fontWeight: 600 }}>Installed{fmtSize(assets.ffmpeg.size)}</span>
                    : <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Not installed — Reels will not generate</span>}
                </span>
                {!assets?.ffmpeg?.installed && (
                  <p style={{ ...mutedStyle, marginTop: 4 }}>
                    Download static amd64 build from{' '}
                    <a href="https://johnvansickle.com/ffmpeg/" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>
                      johnvansickle.com/ffmpeg
                    </a>{' '}
                    and upload the <code style={{ background: 'var(--cream)', padding: '1px 4px', borderRadius: 3 }}>ffmpeg</code> binary below.
                  </p>
                )}
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => pickFile('ffmpeg', '*')}
                disabled={uploading === 'ffmpeg'}
              >
                {uploading === 'ffmpeg' ? 'Uploading…' : assets?.ffmpeg?.installed ? 'Replace' : 'Upload Binary'}
              </button>
            </div>

            {/* Background Music */}
            <p style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Background Music</p>
            <p style={{ ...mutedStyle, marginBottom: 10 }}>
              MP3 tracks played at 25% volume in Reels. A random track is picked each time.{' '}
              Free CC0 music: <a href="https://pixabay.com/music/" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>Pixabay</a>{' '}
              or <a href="https://mixkit.co/free-stock-music/" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>Mixkit</a>
            </p>
            {assets?.music?.length === 0 && (
              <p style={{ ...mutedStyle, fontStyle: 'italic', marginBottom: 8 }}>No music tracks uploaded — Reels will be silent until you add one.</p>
            )}
            {assets?.music?.map(t => (
              <div key={t.key} style={rowStyle}>
                <span style={labelStyle}>{t.name}<span style={mutedStyle}>{fmtSize(t.size)}</span></span>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}
                  onClick={() => remove(t.key, t.name)}>Delete</button>
              </div>
            ))}
            <div style={{ paddingTop: 10, marginBottom: 20 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => pickFile('music', '.mp3')} disabled={uploading === 'music'}>
                {uploading === 'music' ? 'Uploading…' : '+ Add Music Track'}
              </button>
            </div>

            {/* Background Photos */}
            <p style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Background Photos</p>
            <p style={{ ...mutedStyle, marginBottom: 10 }}>
              Custom backgrounds for the flyer (1080×820 px JPG/PNG). When present, these replace AI-generated backgrounds.
            </p>
            {assets?.backgrounds?.length === 0 && (
              <p style={{ ...mutedStyle, fontStyle: 'italic', marginBottom: 8 }}>No photos uploaded — AI will generate backgrounds automatically.</p>
            )}
            {assets?.backgrounds?.map(b => (
              <div key={b.key} style={rowStyle}>
                <span style={labelStyle}>{b.name}<span style={mutedStyle}>{fmtSize(b.size)}</span></span>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}
                  onClick={() => remove(b.key, b.name)}>Delete</button>
              </div>
            ))}
            <div style={{ paddingTop: 10, marginBottom: 20 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => pickFile('background', '.jpg,.jpeg,.png')} disabled={uploading === 'background'}>
                {uploading === 'background' ? 'Uploading…' : '+ Add Background Photo'}
              </button>
            </div>

            {/* Logo & QR Code */}
            <p style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Logo & QR Code</p>
            <p style={{ ...mutedStyle, marginBottom: 10 }}>
              PNG files used in the flyer footer. If not uploaded, the flyer uses a drawn placeholder.
            </p>
            {[
              { key: 'logo',  label: 'Company Logo',   accept: '.png', type: 'logo',  data: assets?.logo },
              { key: 'qr',    label: 'QR Code',        accept: '.png', type: 'qr',    data: assets?.qr   },
            ].map(item => (
              <div key={item.key} style={{ ...rowStyle, borderBottom: 'none', paddingBottom: 0 }}>
                <div>
                  <span style={labelStyle}>{item.label}</span>
                  <span style={{ ...mutedStyle, marginLeft: 8 }}>
                    {item.data?.installed
                      ? <span style={{ color: 'var(--teal)' }}>Uploaded{fmtSize(item.data.size)}</span>
                      : <span style={{ color: 'var(--muted)' }}>Using placeholder</span>}
                  </span>
                </div>
                <button className="btn btn-ghost btn-sm"
                  onClick={() => pickFile(item.type, item.accept)}
                  disabled={uploading === item.type}>
                  {uploading === item.type ? 'Uploading…' : item.data?.installed ? 'Replace' : 'Upload'}
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function BrandVoicePage() {
  const toast     = useToast();
  const fileRef   = useRef();
  const folderRef = useRef();

  const [profile, setProfile]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [analyzing, setAnalyzing]     = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [pastedText, setPastedText]   = useState('');
  const [activeTab, setActiveTab]     = useState('paste');
  const [uploadDone, setUploadDone]   = useState(false);
  const [parsedPosts, setParsedPosts] = useState(null);
  const [parseError, setParseError]   = useState('');

  // Stored posts management
  const [storedPosts, setStoredPosts]   = useState(null); // null = not loaded yet
  const [postsPage, setPostsPage]       = useState(0);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [deletingPosts, setDeletingPosts] = useState(false);
  const PAGE_SIZE = 20;

  useEffect(() => {
    api.getBrandVoice()
      .then(r => setProfile(r.profile))
      .catch(() => toast('Failed to load brand voice', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const loadStoredPosts = useCallback(async () => {
    setLoadingPosts(true);
    try {
      const r = await api.getStoredPosts();
      setStoredPosts(r);
      setPostsPage(0);
    } catch { toast('Failed to load stored posts', 'error'); }
    finally { setLoadingPosts(false); }
  }, []);

  // Load stored posts when profile first appears
  useEffect(() => {
    if (profile && storedPosts === null) loadStoredPosts();
  }, [profile, storedPosts, loadStoredPosts]);

  // Called by AnalysisProgressBar when polling detects the profile is ready
  const handleAnalysisComplete = useCallback((newProfile) => {
    setProfile(newProfile);
    setUploadDone(false); // hide progress card
    toast('Brand voice analysis complete!', 'success');
  }, []);

  // ── Upload helpers ───────────────────────────────────────────────────────
  const uploadPostsToS3 = async (posts) => {
    const { upload_url } = await api.getUploadUrl();
    const res = await fetch(upload_url, {
      method:  'PUT',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(posts),
    });
    if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
    await api.triggerAnalysis();
    toast(`${posts.length} posts uploaded — analysis started`, 'success');
    setUploadDone(true);
    setStoredPosts(null); // will reload after analysis
  };

  const handlePasteSubmit = async () => {
    if (!pastedText.trim() || pastedText.trim().length < 50) {
      toast('Please paste at least a few posts before submitting', 'error');
      return;
    }
    setAnalyzing(true);
    try {
      const res = await api.savePosts({ posts_text: pastedText });
      toast(`Saved ${res.saved} posts — analyzing brand voice now`, 'success');
      setUploadDone(true);
      setStoredPosts(null);
    } catch (e) {
      toast(e.message || 'Failed to save posts', 'error');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFilePicked = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setParseError('');
    setParsedPosts(null);
    setUploading(true);
    try {
      let posts = [];
      const first = files[0];
      if (files.length === 1 && first.name.endsWith('.zip')) {
        posts = await parseZip(first);
      } else {
        posts = await parseFolder(files);
        if (posts.length === 0 && files.length === 1 && first.name.endsWith('.json')) {
          try { posts = parseFBJson(JSON.parse(await first.text())); } catch { /* ignore */ }
        }
      }
      if (posts.length === 0) {
        setParseError('No Facebook posts found. Make sure you\'re selecting the folder that contains your Facebook data export, or a .json file from the "posts" section.');
        return;
      }
      setParsedPosts(posts);
    } catch (e) {
      setParseError(e.message || 'Failed to read files');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!parsedPosts || parsedPosts.length === 0) return;
    setUploading(true);
    try {
      await uploadPostsToS3(parsedPosts);
      setParsedPosts(null);
    } catch (e) {
      toast(e.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
      if (fileRef.current)   fileRef.current.value   = '';
      if (folderRef.current) folderRef.current.value = '';
    }
  };

  const resetFileSelection = () => {
    setParsedPosts(null);
    setParseError('');
    if (fileRef.current)   fileRef.current.value   = '';
    if (folderRef.current) folderRef.current.value = '';
  };

  const handleDeletePosts = async () => {
    if (!window.confirm('Delete all stored Facebook posts? The brand voice profile will remain until you re-analyze with new data.')) return;
    setDeletingPosts(true);
    try {
      await api.deleteStoredPosts();
      setStoredPosts({ count: 0, posts: [], date_range: null });
      toast('Stored posts deleted', 'success');
    } catch (e) {
      toast(e.message || 'Delete failed', 'error');
    } finally {
      setDeletingPosts(false);
    }
  };

  const reanalyze = async () => {
    setAnalyzing(true);
    try {
      await api.triggerAnalysis();
      toast('Re-analysis started — check back in about 60 seconds', 'success');
      setUploadDone(true);
    } catch (e) {
      toast(e.message || 'Failed', 'error');
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) return <p style={{ color: 'var(--muted)' }}>Loading…</p>;

  const pagedPosts   = storedPosts?.posts?.slice(postsPage * PAGE_SIZE, (postsPage + 1) * PAGE_SIZE) || [];
  const totalPages   = Math.ceil((storedPosts?.posts?.length || 0) / PAGE_SIZE);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Brand Voice</h1>
          {profile && (
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 2 }}>
              Version {profile.version} · Updated {toEasternDate(profile.updated_at)} · {profile.sample_size} posts analyzed
            </p>
          )}
        </div>
        {profile && (
          <button className="btn btn-teal" onClick={reanalyze} disabled={analyzing}>
            {analyzing ? 'Running…' : '🔄 Re-analyze'}
          </button>
        )}
      </div>

      {/* ── Analysis running — progress bar ─────────────────────────── */}
      {uploadDone && (
        <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid var(--teal)' }}>
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              <div style={{ fontSize: '1.8rem', flexShrink: 0 }}>⏳</div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, marginBottom: 2 }}>Brand voice analysis is running…</p>
                <p style={{ fontSize: '0.84rem', color: 'var(--muted)', marginBottom: 0 }}>
                  Claude is reading your posts and building your brand voice profile. This page will update automatically.
                </p>
                <AnalysisProgressBar onComplete={handleAnalysisComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Upload / Input section (hidden once analysis starts) ─────── */}
      {!uploadDone && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <h3>{profile ? '📤 Update Brand Data' : '🚀 Build Your Brand Voice'}</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--muted)', margin: 0 }}>
              {profile ? 'Add more posts to improve the analysis' : 'Paste posts or upload your Facebook export to get started'}
            </p>
          </div>
          <div className="card-body">

            <div className="filter-bar" style={{ marginBottom: 20 }}>
              <button className={`filter-btn${activeTab === 'paste' ? ' active' : ''}`} onClick={() => setActiveTab('paste')}>
                ✏️ Paste Posts
              </button>
              <button className={`filter-btn${activeTab === 'file' ? ' active' : ''}`} onClick={() => { setActiveTab('file'); resetFileSelection(); }}>
                📁 Facebook Export
              </button>
            </div>

            {/* Paste tab */}
            {activeTab === 'paste' && (
              <div>
                <p style={{ fontSize: '0.88rem', color: 'var(--muted)', marginBottom: 12 }}>
                  Copy and paste your Facebook posts below. Separate multiple posts with{' '}
                  <code style={{ background: 'var(--cream)', padding: '1px 6px', borderRadius: 4 }}>---</code> on its own line.
                </p>
                <textarea
                  className="form-input"
                  style={{ minHeight: 220, fontFamily: 'inherit', fontSize: '0.88rem', lineHeight: 1.6, resize: 'vertical' }}
                  placeholder={PLACEHOLDER}
                  value={pastedText}
                  onChange={e => setPastedText(e.target.value)}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                    {pastedText.split('---').filter(p => p.trim().length > 20).length} posts detected
                  </span>
                  <button className="btn btn-primary" onClick={handlePasteSubmit} disabled={analyzing || pastedText.trim().length < 50}>
                    {analyzing ? 'Analyzing…' : '✨ Analyze Brand Voice'}
                  </button>
                </div>
              </div>
            )}

            {/* Facebook Export tab */}
            {activeTab === 'file' && (
              <div>
                <div style={{ background: 'var(--cream)', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
                  <p style={{ fontSize: '0.88rem', fontWeight: 600, marginBottom: 6 }}>Two ways to upload your Facebook data:</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: '0.84rem', color: 'var(--muted)' }}>
                    <div><strong style={{ color: 'var(--navy)' }}>Option A — Folder</strong><br />Extract your .zip export, then click <strong>Select Folder</strong>.</div>
                    <div><strong style={{ color: 'var(--navy)' }}>Option B — ZIP file</strong><br />Upload the .zip file directly — no extraction needed.</div>
                  </div>
                  <p style={{ fontSize: '0.81rem', color: 'var(--teal)', marginTop: 10, marginBottom: 0 }}>
                    The app automatically finds your posts. Messages, photos, and other data are ignored.
                  </p>
                </div>

                {parsedPosts && (
                  <div style={{ border: '2px solid var(--teal)', borderRadius: 10, padding: '18px 20px', marginBottom: 16, background: 'rgba(42,127,127,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <p style={{ fontWeight: 600, marginBottom: 4, color: 'var(--teal)' }}>✓ Found {parsedPosts.length} posts</p>
                        <p style={{ fontSize: '0.83rem', color: 'var(--muted)', margin: 0 }}>
                          Date range: {parsedPosts.reduce((a, p) => p.date < a ? p.date : a, '9999')} → {parsedPosts.reduce((a, p) => p.date > a ? p.date : a, '0000')}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button className="btn btn-ghost btn-sm" onClick={resetFileSelection}>Change file</button>
                        <button className="btn btn-primary" onClick={handleConfirmUpload} disabled={uploading}>
                          {uploading ? 'Uploading…' : `✨ Analyze ${parsedPosts.length} Posts`}
                        </button>
                      </div>
                    </div>
                    {parsedPosts.slice(0, 2).map((p, i) => (
                      <div key={i} style={{ marginTop: 12, background: 'var(--cream)', borderRadius: 6, padding: '10px 14px', fontSize: '0.83rem', color: 'var(--muted)', lineHeight: 1.5 }}>
                        <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>{p.date} · </span>
                        {p.content.length > 160 ? p.content.slice(0, 160) + '…' : p.content}
                      </div>
                    ))}
                  </div>
                )}

                {parseError && (
                  <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: '0.85rem', color: 'var(--danger)' }}>
                    {parseError}
                  </div>
                )}

                {!parsedPosts && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ border: '2px dashed var(--border)', borderRadius: 12, padding: '24px 20px', textAlign: 'center', cursor: 'pointer' }}
                         onClick={() => folderRef.current?.click()}>
                      <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>📂</div>
                      <p style={{ fontWeight: 600, marginBottom: 4, fontSize: '0.9rem' }}>{uploading ? 'Scanning…' : 'Select Folder'}</p>
                      <p style={{ fontSize: '0.78rem', color: 'var(--muted)', margin: 0 }}>Choose the extracted Facebook export folder</p>
                      <input ref={folderRef} type="file" style={{ display: 'none' }} webkitdirectory="true" directory="true" multiple onChange={handleFilePicked} />
                    </div>
                    <div style={{ border: '2px dashed var(--border)', borderRadius: 12, padding: '24px 20px', textAlign: 'center', cursor: 'pointer' }}
                         onClick={() => fileRef.current?.click()}
                         onDragOver={e => e.preventDefault()}
                         onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFilePicked({ target: { files: [f] } }); }}>
                      <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>🗜️</div>
                      <p style={{ fontWeight: 600, marginBottom: 4, fontSize: '0.9rem' }}>{uploading ? 'Scanning…' : 'Upload ZIP or JSON'}</p>
                      <p style={{ fontSize: '0.78rem', color: 'var(--muted)', margin: 0 }}>Drop the .zip export or a posts .json file</p>
                      <input ref={fileRef} type="file" accept=".zip,.json" style={{ display: 'none' }} onChange={handleFilePicked} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Stored posts management ──────────────────────────────────── */}
      {profile && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3>📦 Uploaded Posts</h3>
              <p style={{ fontSize: '0.82rem', color: 'var(--muted)', margin: 0 }}>
                {storedPosts === null
                  ? 'Loading…'
                  : storedPosts.count === 0
                    ? 'No posts stored'
                    : `${storedPosts.count} posts stored${storedPosts.date_range ? ` · ${storedPosts.date_range.earliest} → ${storedPosts.date_range.latest}` : ''}`
                }
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {storedPosts === null && (
                <button className="btn btn-ghost btn-sm" onClick={loadStoredPosts} disabled={loadingPosts}>
                  {loadingPosts ? 'Loading…' : 'Load Posts'}
                </button>
              )}
              {storedPosts && storedPosts.count > 0 && (
                <button className="btn btn-ghost btn-sm" onClick={loadStoredPosts} disabled={loadingPosts}>
                  {loadingPosts ? '…' : '↻ Refresh'}
                </button>
              )}
              {storedPosts && storedPosts.count > 0 && (
                <button
                  className="btn btn-sm"
                  style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger)' }}
                  onClick={handleDeletePosts}
                  disabled={deletingPosts}
                >
                  {deletingPosts ? 'Deleting…' : '🗑 Clear Posts'}
                </button>
              )}
            </div>
          </div>

          {storedPosts && storedPosts.count > 0 && (
            <div className="card-body" style={{ paddingTop: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pagedPosts.map((post, i) => (
                  <div key={post.id || i} style={{ background: 'var(--cream)', borderRadius: 8, padding: '10px 14px', fontSize: '0.85rem', lineHeight: 1.55 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: 4 }}>{post.date}</div>
                    <div style={{ color: 'var(--navy)' }}>
                      {post.content.length > 240 ? post.content.slice(0, 240) + '…' : post.content}
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16, fontSize: '0.85rem' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPostsPage(p => Math.max(0, p - 1))} disabled={postsPage === 0}>← Prev</button>
                  <span style={{ color: 'var(--muted)' }}>Page {postsPage + 1} of {totalPages}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPostsPage(p => Math.min(totalPages - 1, p + 1))} disabled={postsPage === totalPages - 1}>Next →</button>
                </div>
              )}

              {storedPosts.count > 100 && (
                <p style={{ fontSize: '0.78rem', color: 'var(--muted)', textAlign: 'center', marginTop: 8 }}>
                  Showing first 100 of {storedPosts.count} stored posts
                </p>
              )}
            </div>
          )}

          {storedPosts && storedPosts.count === 0 && (
            <div className="card-body">
              <p style={{ color: 'var(--muted)', fontSize: '0.85rem', textAlign: 'center' }}>
                No posts file found. Upload your Facebook export above to rebuild the analysis.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Brand voice profile ─────────────────────────────────────── */}
      {!profile && !uploadDone ? (
        <div className="card">
          <div className="empty-state" style={{ padding: 48 }}>
            <div className="empty-state-icon">🎨</div>
            <p>Use the form above to add your posts and build your brand voice profile.</p>
          </div>
        </div>
      ) : profile ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          <div className="card">
            <div className="card-header"><h3>Tone & Personality</h3></div>
            <div className="card-body"><p style={{ fontSize: '0.95rem', lineHeight: 1.7 }}>{profile.tone_description}</p></div>
          </div>

          <div className="card">
            <div className="card-header"><h3>Core Themes</h3></div>
            <div className="card-body">
              <div className="tag-list">{(profile.core_themes || []).map(t => <span key={t} className="tag">{t}</span>)}</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3>Hashtags</h3></div>
            <div className="card-body">
              <div className="tag-list">{(profile.hashtags || []).map(h => <span key={h} className="tag teal">{h}</span>)}</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3>Emojis & CTAs</h3></div>
            <div className="card-body">
              <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: 10 }}>Emojis</p>
              <div className="tag-list" style={{ marginBottom: 16 }}>
                {(profile.emojis || []).map(e => <span key={e} className="tag" style={{ fontSize: '1.1rem' }}>{e}</span>)}
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: 10 }}>Calls to action</p>
              <div className="tag-list">{(profile.cta_patterns || []).map(c => <span key={c} className="tag">{c}</span>)}</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3>Posting Cadence</h3></div>
            <div className="card-body">
              <p style={{ fontSize: '0.9rem' }}>{profile.posting_cadence}</p>
              <p style={{ marginTop: 10, fontSize: '0.85rem', color: 'var(--muted)' }}>
                Average post length: <strong>{profile.avg_post_length} characters</strong>
              </p>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3>Topics to Avoid</h3></div>
            <div className="card-body">
              <div className="tag-list">
                {(profile.topics_to_avoid || []).map(t => (
                  <span key={t} className="tag" style={{ background: 'var(--danger-bg)', borderColor: 'var(--danger)', color: 'var(--danger)' }}>{t}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <div className="card-header"><h3>Sample Posts</h3></div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {(profile.sample_posts || []).map((s, i) => (
                <div key={i} style={{ background: 'var(--cream)', padding: '14px 16px', borderRadius: 8, fontSize: '0.9rem', lineHeight: 1.6 }}>{s}</div>
              ))}
            </div>
          </div>

        </div>
      ) : null}

      <ReelAssetsSection />
    </>
  );
}
